import { AnalysisMain, GeneralUtils } from "asset-analysis";
import useAssetAnalysisOverview from "../../../hooks/useAssetAnalysisOverview";
import { ReportableBase } from "../base/ReportableBase";
import { RequestResult } from "asset-analysis/lib/@types/incoming/request.response";
import { eAssetType } from "asset-analysis/lib/@types/config/asset.enum";
import { AnalysisReadableResult } from "asset-analysis/lib/@types/outcoming/analysis-readable-result";
import { GeneralOverview } from "asset-analysis/lib/@types/analysis/utils/general";

export class ReportAssetAnalysisOverview extends ReportableBase<GeneralOverview> {
  constructor(
    private readonly encodedAssetId: string,
    private readonly assetType: eAssetType,
    private readonly requestResult: RequestResult
  ) {
    const investorType =
      requestResult.analysisResults.Informations.investorType;
    const lastSaveKeyName =
      "asset-analysis-overview_last_save_details" + investorType;
    const lastReportedJsonKeyName =
      "asset-analysis-overview_last_report_json" + investorType;

    super(lastSaveKeyName, lastReportedJsonKeyName, encodedAssetId, 1);
  }

  public async report(data: GeneralOverview): Promise<boolean> {
    const isAlreadyReported = this.isAlreadyReported(data);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { storeReport } = useAssetAnalysisOverview();

    if (!isAlreadyReported) {
      const storeData = await storeReport(data, this.encodedAssetId);

      if (storeData) {
        this.updateLastReportedJSON(data);
        this.updateLastSave();

        return true;
      }
    }

    return false;
  }

  public getReportableData(): GeneralOverview {
    const analysisModule = new AnalysisMain(this.requestResult, {
      indications: {
        technical: true,
        fundamental: true,
        extra: true,
        historical: true,
        health: true,
      },
      impressions: {
        fundamental: false,
        historical: false,
        technical: false,
        health: false,
      },
      asset: {
        type: this.assetType,
        encodedId: this.encodedAssetId,
      },
    });

    analysisModule.process();
    const reportableAnalysisResult = analysisModule.getReadableResult();

    const { indications } = reportableAnalysisResult as AnalysisReadableResult;

    return {
      ...GeneralUtils.getGeneralOverview(indications, {
        extra: true,
        fundamental: true,
        health: true,
        technical: true,
      }),
    };
  }
}
